import { useEffect, useMemo, useState } from "react";
import {
  groupServiceVariants,
  normalizeSearch,
  type CategoryWithServices,
} from "@shared/modules/x-atelier";
import {
  DEFAULT_ONLINE_START_TIMES,
  type QuotaExemption,
  type SiteSettings,
} from "@shared/modules/x-site";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input } from "../../../core/components/ui/input";
import { getCatalogue } from "../../x-atelier/lib/api";

// Règles des réservations prises sans employé (site, assistant du site, Léo) :
// combien par demi-journée, à quelles heures, quels services s'ajoutent hors
// quota, et à partir de quelle durée un soin enjambe la pause de midi. Tout
// reste réglable : l'atelier les ajuste selon la charge des concessionnaires.

type Setter = <K extends keyof SiteSettings>(key: K, value: SiteSettings[K]) => void;

interface ServiceChoice {
  key: string;
  name: string;
  category: string;
}

/** Services que l'atelier a demandé de laisser hors quota (s'ils existent au catalogue). */
const SUGGESTED = [
  /^forfait protection exterieure?\b/,
  /^nettoyage exterieur de base\b/,
  /^shampoo?ing moteur\b/,
];

/** Nombre proposé à l'ajout d'un service hors quota, par demi-journée. */
const DEFAULT_PER_HALF_DAY = 2;

function choicesOf(categories: CategoryWithServices[]): ServiceChoice[] {
  const byKey = new Map<string, ServiceChoice>();
  for (const c of categories)
    for (const g of groupServiceVariants(c.services.filter((s) => s.active)))
      if (!byKey.has(g.key)) byKey.set(g.key, { key: g.key, name: g.name, category: c.name });
  return [...byKey.values()];
}

const hourLabel = (hhmm: string) => {
  const [h, m] = hhmm.split(":");
  return m === "00" ? `${Number(h)} h` : `${Number(h)} h ${m}`;
};

const countOrNull = (value: string): number | null =>
  value.trim() === "" ? null : Math.max(0, Math.round(Number(value)));

export function AutomaticBookingCard({ draft, set }: { draft: SiteSettings; set: Setter }) {
  const [choices, setChoices] = useState<ServiceChoice[] | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let alive = true;
    getCatalogue()
      .then((categories) => alive && setChoices(choicesOf(categories)))
      .catch(() => alive && setChoices([]));
    return () => {
      alive = false;
    };
  }, []);

  const times = draft.onlineStartTimes ?? [];
  const exemptions = draft.quotaExemptions ?? [];
  const chosen = new Set(exemptions.map((e) => e.key));
  const known = new Set((choices ?? []).map((c) => c.key));

  const results = useMemo(() => {
    const q = normalizeSearch(query);
    if (!choices || !q) return [];
    const taken = new Set(exemptions.map((e) => e.key));
    return choices
      .filter(
        (c) =>
          !taken.has(c.key) &&
          (normalizeSearch(c.name).includes(q) || normalizeSearch(c.category).includes(q)),
      )
      .slice(0, 8);
  }, [choices, query, exemptions]);

  const suggestions = (choices ?? []).filter(
    (c) => !chosen.has(c.key) && SUGGESTED.some((re) => re.test(normalizeSearch(c.name))),
  );

  const setTimes = (next: string[]) => set("onlineStartTimes", next);
  const setExemptions = (next: QuotaExemption[]) => set("quotaExemptions", next);
  const add = (c: ServiceChoice) => {
    setExemptions([...exemptions, { key: c.key, label: c.name, perHalfDay: DEFAULT_PER_HALF_DAY }]);
    setQuery("");
  };

  return (
    <Card
      title="Réservations automatiques"
      subtitle="Prises par le site, l’assistant du site et Léo. Les employés au CRM n’y sont pas soumis ; les présences, la capacité et la pause de midi s’appliquent toujours."
    >
      <div className="site-admin__stack">
        <div className="form-grid">
          <Field label="Rendez-vous par matinée" hint="Au plus. Vide : sans limite.">
            <Input
              type="number"
              min={0}
              max={50}
              value={draft.onlineMorningMax ?? ""}
              onChange={(e) => set("onlineMorningMax", countOrNull(e.target.value))}
            />
          </Field>
          <Field label="Rendez-vous par après-midi" hint="Au plus. Vide : sans limite.">
            <Input
              type="number"
              min={0}
              max={50}
              value={draft.onlineAfternoonMax ?? ""}
              onChange={(e) => set("onlineAfternoonMax", countOrNull(e.target.value))}
            />
          </Field>
          <div className="form-grid__full">
            <Field
              label="Soin long : plus de (minutes)"
              hint={
                draft.lunchBreak
                  ? "Commencé le matin, il s’interrompt pendant la pause de midi et reprend après : sa fin est repoussée d’autant. Vaut aussi au CRM. Vide : jamais."
                  : "Sans pause de midi, ce réglage n’a pas d’effet."
              }
            >
              <Input
                type="number"
                min={30}
                max={1440}
                step={30}
                value={draft.longServiceMinutes ?? ""}
                onChange={(e) =>
                  set(
                    "longServiceMinutes",
                    e.target.value.trim() === "" ? null : Math.round(Number(e.target.value)),
                  )
                }
              />
            </Field>
          </div>
        </div>

        <div className="site-admin__closures">
          <h3 className="site-admin__subtitle">Heures de début</h3>
          {times.length ? (
            <ul className="site-admin__times">
              {times.map((time, i) => (
                <li key={i} className="site-admin__time">
                  <Input
                    type="time"
                    aria-label={`Heure de début ${i + 1}`}
                    value={time}
                    onChange={(e) => setTimes(times.map((t, j) => (j === i ? e.target.value : t)))}
                  />
                  <Button
                    type="button"
                    variant="ghost-danger"
                    size="sm"
                    aria-label={`Retirer ${hourLabel(time)}`}
                    onClick={() => setTimes(times.filter((_, j) => j !== i))}
                  >
                    Retirer
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
          <p className="site-admin__note">
            {times.length
              ? `Un rendez-vous automatique commence seulement à ${times
                  .filter(Boolean)
                  .map(hourLabel)
                  .join(", ")}.`
              : `Aucune heure imposée : un créneau toutes les ${draft.slotStepMin} minutes.`}
          </p>
          <div className="site-admin__actions">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon={<Icon name="plus" size={14} />}
              onClick={() => setTimes([...times, "10:00"])}
            >
              Ajouter une heure
            </Button>
            {times.join() !== DEFAULT_ONLINE_START_TIMES.join() && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setTimes([...DEFAULT_ONLINE_START_TIMES])}
              >
                Rétablir 8 h, 9 h, 13 h, 14 h
              </Button>
            )}
          </div>
        </div>

        <div className="site-admin__closures">
          <h3 className="site-admin__subtitle">Services hors quota</h3>
          <p className="site-admin__note">
            Réservables en plus des rendez-vous ci-dessus, à toute heure, jusqu’au nombre indiqué
            par demi-journée. Un panier qui mêle un de ces services et un autre suit les règles
            ordinaires.
          </p>
          {exemptions.length ? (
            <ul className="site-admin__exemptions">
              {exemptions.map((e, i) => (
                <li key={e.key} className="site-admin__exemption">
                  <span className="site-admin__exemption-name">
                    {e.label || e.key}
                    {choices && !known.has(e.key) ? (
                      <small className="site-admin__note"> · introuvable au catalogue</small>
                    ) : null}
                  </span>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    aria-label={`${e.label || e.key} : par demi-journée`}
                    value={e.perHalfDay}
                    onChange={(ev) =>
                      setExemptions(
                        exemptions.map((x, j) =>
                          j === i
                            ? { ...x, perHalfDay: Math.max(1, Math.round(Number(ev.target.value))) }
                            : x,
                        ),
                      )
                    }
                  />
                  <span className="site-admin__note">par demi-journée</span>
                  <Button
                    type="button"
                    variant="ghost-danger"
                    size="sm"
                    onClick={() => setExemptions(exemptions.filter((_, j) => j !== i))}
                  >
                    Retirer
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="site-admin__note">Aucun : tous les services suivent le quota.</p>
          )}
          {suggestions.length ? (
            <div className="site-admin__suggestions">
              <span className="site-admin__note">Demandés par l’atelier :</span>
              {suggestions.map((c) => (
                <Button
                  key={c.key}
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon={<Icon name="plus" size={14} />}
                  onClick={() => add(c)}
                >
                  {c.name}
                </Button>
              ))}
            </div>
          ) : null}
          <Field label="Ajouter un service">
            <Input
              type="search"
              value={query}
              placeholder={
                choices ? "Nom du service ou de la catégorie" : "Chargement du catalogue…"
              }
              disabled={!choices}
              onChange={(e) => setQuery(e.target.value)}
            />
          </Field>
          {query.trim() ? (
            results.length ? (
              <ul className="site-admin__results">
                {results.map((c) => (
                  <li key={c.key}>
                    <button type="button" className="site-admin__result" onClick={() => add(c)}>
                      <span>{c.name}</span>
                      <small>{c.category}</small>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="site-admin__note">Aucun service ne correspond.</p>
            )
          ) : null}
        </div>
      </div>
    </Card>
  );
}
